/**
 * Settings > Server (desktop): which server this app talks to, for pointing
 * the packaged app at the VPS. The shell validates and saves it
 * (`setServerOrigin`); the page then signs out of the old server and reloads
 * against the new one. On the web the server is simply the site you opened.
 * (The new-account note went with the new-account period on 2026-10-28.)
 */
import { useState } from 'react';
import { useT } from '../i18n';
import { desktopBridge, isPackagedApp, normalizeServerOrigin, serverHost } from '../lib/config';
import { useReach } from '../store/reach';
import { signOut } from '../store/session';
import { Btn, Icon } from '../ui/kit';
import { Dialog } from '../ui/overlay';
import { SetGroup } from './Settings';

/**
 * The server line, honest about it (QA DT-BUG-03, DT-BUG-04): a packaged
 * app with no address asks for one (its own `tauri.localhost` is never the
 * server); "Connected to" only once a request got an answer, "Can't reach"
 * when the last one got none, and just the address before anything was
 * asked.
 */
function ServerStatus({
  host,
  missing,
  inline = false,
}: {
  host: string;
  missing: boolean;
  inline?: boolean;
}) {
  const { t } = useT();
  const reach = useReach((s) => s.server);
  if (missing)
    return (
      <p className="ns-note warn" role="alert">
        <Icon name="globe" /> {t('um.server.needed')}
      </p>
    );
  if (reach === 'down')
    return (
      <p className="ns-note warn" role="status">
        <Icon name="alert" /> {t('um.server.unreachable', { host })}
      </p>
    );
  const text = (
    <>
      <Icon name="globe" />{' '}
      {t(reach === 'ok' ? 'um.server.current' : 'um.server.address', { host })}
    </>
  );
  return inline ? <span className="ns-muted">{text}</span> : <p>{text}</p>;
}

/** Settings › Server; `bare` leaves out the page title (in the sign-in screen's dialog). */
export function ServerSection({ bare = false }: { bare?: boolean }) {
  const { t } = useT();
  const bridge = desktopBridge();
  const [value, setValue] = useState(bridge?.serverOrigin ?? '');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const host = serverHost();
  if (!bridge) {
    return (
      <>
        {bare ? null : <h2>{t('um.server.title')}</h2>}
        <SetGroup>
          <p className="nx-set-note">
            <Icon name="globe" />
            <span>{t('um.server.webOnly', { host })}</span>
          </p>
        </SetGroup>
      </>
    );
  }
  const source = bridge.serverOriginSource ?? null;
  const locked = source === 'env';
  const missing = isPackagedApp() && !bridge.serverOrigin;
  const current = bridge.serverOrigin ?? null;
  const typed = value.trim();
  // The address already in use: saving it would only sign you out (QA DT-BUG-10).
  const unchanged = !!typed && normalizeServerOrigin(typed) === current;
  const save = async (origin: string | null) => {
    setErr(null);
    const normal = origin === null ? null : normalizeServerOrigin(origin);
    if (origin !== null && !normal) {
      setErr(t('um.server.invalid'));
      return;
    }
    if (origin !== null && normal === current) return;
    if (!bridge.setServerOrigin) return;
    setBusy(true);
    try {
      await bridge.setServerOrigin(normal);
    } catch (e) {
      setBusy(false);
      setErr(typeof e === 'string' ? e : t('um.server.invalid'));
      return;
    }
    // the session belongs to the old server: end it there, then load against the new one
    await signOut();
    location.reload();
  };
  return (
    <>
      {bare ? null : <h2>{t('um.server.title')}</h2>}
      <SetGroup>
        <div className="ns-server">
          <ServerStatus host={host} missing={missing} />
          {missing ? null : (
            <p className="ns-muted">
              {source === 'env'
                ? t('um.server.fromEnv')
                : source === 'settings'
                  ? t('um.server.fromSettings')
                  : source === 'build'
                    ? t('um.server.fromBuild')
                    : t('um.server.sameOrigin')}
            </p>
          )}
          {!bridge.setServerOrigin ? (
            <p className="ns-note warn">{t('um.server.oldShell')}</p>
          ) : (
            <form
              className="ns-field"
              onSubmit={(e) => {
                e.preventDefault();
                void save(typed);
              }}
            >
              <label>
                <span className="ns-label">{t('um.server.origin')}</span>
                <input
                  className="nx-input"
                  dir="ltr"
                  inputMode="url"
                  placeholder="https://chat.example"
                  value={value}
                  disabled={locked || busy}
                  aria-invalid={!!err || undefined}
                  onChange={(e) => setValue(e.target.value)}
                />
              </label>
              <span className="ns-help">{t('um.server.originHelp')}</span>
              <span className="ns-help">{t('um.server.signOutFirst')}</span>
              {err ? (
                <p className="ns-err" role="alert">
                  {err}
                </p>
              ) : null}
              <div className="ns-row">
                <Btn
                  kind="primary"
                  size="sm"
                  type="submit"
                  busy={busy}
                  disabled={locked || !typed || unchanged}
                >
                  {t('um.server.save')}
                </Btn>
                {source === 'settings' ? (
                  <Btn kind="ghost" size="sm" disabled={busy} onClick={() => void save(null)}>
                    {t('um.server.reset')}
                  </Btn>
                ) : null}
              </div>
            </form>
          )}
        </div>
      </SetGroup>
    </>
  );
}

/**
 * The sign-in screen's server line (desktop only): a packaged app without a
 * built-in address must be pointed at the server before anyone can sign in.
 */
export function DesktopServerLink() {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const bridge = desktopBridge();
  if (!bridge) return null;
  const missing = isPackagedApp() && !bridge.serverOrigin;
  return (
    <div className="ns-auth-server">
      <ServerStatus host={serverHost()} missing={missing} inline />
      <button type="button" className="ns-link" onClick={() => setOpen(true)}>
        {t(missing ? 'um.server.set' : 'um.server.change')}
      </button>
      {open ? (
        <Dialog title={t('um.server.title')} width={560} onClose={() => setOpen(false)}>
          <ServerSection bare />
        </Dialog>
      ) : null}
    </div>
  );
}
